import { FileText } from 'lucide-react';
import { PageHeader } from '@/shared/components/ui/PageHeader';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Modal } from '@/shared/components/ui/Modal';
import { Select } from '@/shared/components/ui/Select';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { Badge } from '@/shared/components/ui/Badge';
import { EmailSendModal } from '@/shared/components/ui/EmailSendModal';
import { useUser } from '@/shared/hooks/useAuth';
import { Send, Eye, Plus, CheckCircle2, Link as LinkIcon, BriefcaseBusiness } from 'lucide-react';
import toast from 'react-hot-toast';
import { useMasterDepartments } from '@/features/recruitment/api';

interface OfferLetter {
  _id: string;
  candidateId: string;
  /** Set when this offer was auto-created from a Recruitment application
   * reaching the 'hired' stage (see api-recruitment/index.js bridge). Null
   * for offers created standalone from this page. */
  sourceApplicationId?: string | null;
  candidateName: string;
  candidateEmail: string;
  designation: string;
  department: string;
  joiningDate: string;
  salaryStructure: { basic: number; hra: number; da: number; specialAllowance: number; conveyance: number; medicalAllowance: number; grossSalary: number; pf: number; esi: number; professionalTax: number; totalDeductions: number; netPay: number };
  ctcAnnual: number;
  targetRevenue: number;
  incentivePercent: number;
  probationPeriod: string;
  noticePeriod: string;
  workLocation: string;
  reportingTo: string;
  status: string;
  createdAt: string;
}

function useOfferLetters() {
  return useQuery({
    queryKey: ['hr', 'offer-letters'],
    queryFn: async () => { const { data } = await api.get('/api/hr-offers'); return data as { data: OfferLetter[] }; },
  });
}

function useCreateOfferLetter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => { const { data } = await api.post('/api/hr-offers', payload); return data; },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'offer-letters'] }),
  });
}

function useSendOffer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => { const { data } = await api.post(`/api/hr-offers/${id}/send`); return data; },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'offer-letters'] }),
  });
}

// There is no candidate-facing acceptance portal yet, so HR records the
// candidate's acceptance on their behalf (e.g. after a verbal/email
// confirmation). Accepting is what actually triggers onboarding: it advances
// the linked recruitment application to Hired (if any) and creates the
// OnboardingCandidate + E-Joining link.
function useAcceptOffer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<OfferLetter & { onboardingLink?: string | null }>(`/api/hr-offers/${id}/accept`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'offer-letters'] }),
  });
}

// ── HR Document Templates hooks ──────────────────────────────────────────────

interface HrDocTemplate {
  _id: string;
  id: string;
  department: string;
  docType: string;
  name: string;
  bodyHtml: string;
  updatedAt: string;
}

function useHrTemplates(department?: string, docType?: string) {
  return useQuery({
    queryKey: ['hr', 'templates', department, docType],
    queryFn: async () => {
      const params = new URLSearchParams();
      // The route filters on docType only; department is matched client-side
      // below. Sending it did nothing, which made the filter look broken.
      if (docType) params.set('docType', docType);
      // GET /api/hr/templates answers `{ data: [...] }`, not a bare array
      // (src/api-hr-templates.js). Returning the envelope straight through made
      // `templates` an object, so `templates.find(...)` threw "m.find is not a
      // function" — the ErrorBoundary swallowed it and the modal never appeared.
      // A `= []` default could not save it either: the value was defined, just
      // the wrong shape. Both shapes are accepted so the hook survives the
      // envelope being removed later.
      const { data } = await api.get<{ data?: HrDocTemplate[] } | HrDocTemplate[]>(
        `/api/hr/templates?${params}`,
      );
      if (Array.isArray(data)) return data;
      return Array.isArray(data?.data) ? data.data : [];
    },
  });
}

export function OfferLetterPage() {
  const user = useUser();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'superadmin' || user?.role === 'admin' || (user?.department === 'hr');
  const { data, isLoading } = useOfferLetters();
  const createMut = useCreateOfferLetter();
  const sendMut = useSendOffer();
  const acceptMut = useAcceptOffer();
  const [showNew, setShowNew] = useState(false);
  const [onboardingLinkFor, setOnboardingLinkFor] = useState<{ name: string; link: string } | null>(null);
  const [emailDraft, setEmailDraft] = useState<{ to: string; subject: string; html: string } | null>(null);

  // Candidate picker for auto-fill
  const { data: onbCandidates } = useQuery({
    queryKey: ['onboarding-candidates-picker'],
    queryFn: async () => { const { data: r } = await api.get('/api/hr-onboarding-candidates/candidates?limit=100'); return (r?.data || []) as { _id: string; firstName: string; lastName: string; email: string; designation: string; department: string; dateOfJoining: string }[]; },
  });
  const [selectedCandidate, setSelectedCandidate] = useState('');

  // Form state
  const [form, setForm] = useState({
    candidateName: '', candidateEmail: '', designation: '', department: '',
    joiningDate: '', workLocation: '', reportingTo: '',
    ctcAnnual: '0',
    targetRevenue: '0', incentivePercent: '0',
    probationPeriod: '3 months', noticePeriod: '1 month', additionalTerms: '',
  });
  const setField = (k: string, v: string) => setForm(p => ({ ...p, [k]: v }));

  // Check if the selected department has a custom offer letter template
  const { data: deptTemplates = [] } = useHrTemplates(form.department || undefined, 'offer_letter');
  const hasDeptTemplate = form.department && deptTemplates.length > 0;
  const { data: departments = [] } = useMasterDepartments();

  const handleCreate = async () => {
    if (!form.candidateName || !form.designation || !form.department || !form.joiningDate) {
      toast.error('Name, designation, department, and joining date are required.');
      return;
    }
    try {
      await createMut.mutateAsync({
        candidateId: `manual-${Date.now()}`,
        candidateName: form.candidateName,
        candidateEmail: form.candidateEmail,
        designation: form.designation,
        department: form.department,
        joiningDate: form.joiningDate,
        workLocation: form.workLocation,
        reportingTo: form.reportingTo,
        ctcAnnual: Number(form.ctcAnnual) || 0,
        targetRevenue: Number(form.targetRevenue),
        incentivePercent: Number(form.incentivePercent),
        probationPeriod: form.probationPeriod,
        noticePeriod: form.noticePeriod,
        additionalTerms: form.additionalTerms,
      });
      toast.success('Offer letter created as draft.');
      setShowNew(false);
    } catch (err: any) { toast.error(err?.response?.data?.error || 'Failed.'); }
  };

  if (!isAdmin) return <div className="p-6 text-center text-muted-foreground">Access denied. HR Admin only.</div>;
  if (isLoading) return <div className="p-6"><Skeleton className="h-[400px]" /></div>;
  const offers = data?.data || [];

  const statusBadge = (s: string) => {
    const c: Record<string, string> = { draft: 'bg-gray-100 text-gray-600', sent: 'bg-info/10 text-info-text', accepted: 'bg-success/10 text-success-text', rejected: 'bg-danger/10 text-danger-text', withdrawn: 'bg-warning/10 text-warning-text' };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${c[s] || 'bg-gray-100'}`}>{s}</span>;
  };

  const handleAccept = (o: OfferLetter) => {
    acceptMut.mutate(o._id, {
      onSuccess: (result: any) => {
        toast.success(`${o.candidateName}'s offer marked as accepted.`);
        if (result.onboardingLink) {
          setOnboardingLinkFor({ name: o.candidateName, link: `${window.location.origin}${result.onboardingLink}` });
        }
        if (result.emailDraft) {
          setEmailDraft(result.emailDraft);
        }
      },
      onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to accept offer.'),
    });
  };

  return (
    <div className="space-y-4 pb-16">
      <PageHeader icon={FileText} eyebrow="People" title="Offer Letters" description={<>Create, send, and manage offer letters. Print as PDF from the preview.</>} />

      <Modal open={showNew} onClose={() => setShowNew(false)} title="New Offer Letter" size="lg" footer={
        <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setShowNew(false)}>Cancel</Button><Button onClick={handleCreate} disabled={createMut.isPending}>{createMut.isPending ? 'Creating...' : 'Create Draft'}</Button></div>
      }>
        <div className="space-y-4 py-3 max-h-[60vh] overflow-auto">
          <div className="p-3 rounded-lg border bg-info/10 text-sm text-info-text mb-2">
            <strong>Quick fill:</strong> Select a candidate from onboarding to auto-fill their details.
            <select className="ml-2 border rounded px-2 py-1 text-xs bg-white" value={selectedCandidate} onChange={e => {
              const c = (onbCandidates || []).find(x => x._id === e.target.value);
              if (c) { setForm(f => ({ ...f, candidateName: `${c.firstName} ${c.lastName}`.trim(), candidateEmail: c.email || '', designation: c.designation || '', department: c.department || '', dateOfJoining: c.dateOfJoining?.slice(0, 10) || '' })); }
              setSelectedCandidate(e.target.value);
            }}>
              <option value="">— Select candidate —</option>
              {(onbCandidates || []).map(c => <option key={c._id} value={c._id}>{c.firstName} {c.lastName} ({c.designation || 'TBD'})</option>)}
            </select>
          </div>
          <h3 className="text-sm font-semibold">Candidate Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input name="ol-name" label="Candidate Name *" value={form.candidateName} onChange={e => setField('candidateName', e.target.value)} />
            <Input name="ol-email" label="Email" value={form.candidateEmail} onChange={e => setField('candidateEmail', e.target.value)} />
            <Input name="ol-designation" label="Designation *" value={form.designation} onChange={e => setField('designation', e.target.value)} placeholder="e.g. Business Development Executive" />
            <div>
              <Select
                label="Department *"
                options={departments.map((d) => ({ label: d.label, value: d.label }))}
                value={form.department}
                onChange={(e) => setField('department', e.target.value)}
              />
              {hasDeptTemplate && (
                <Badge tone="info" className="mt-1 text-xs">Custom Department Template Active</Badge>
              )}
            </div>
            <Input name="ol-joining" label="Joining Date *" type="date" value={form.joiningDate} onChange={e => setField('joiningDate', e.target.value)} />
            <Input name="ol-location" label="Work Location" value={form.workLocation} onChange={e => setField('workLocation', e.target.value)} />
            <Input name="ol-reporting" label="Reporting To" value={form.reportingTo} onChange={e => setField('reportingTo', e.target.value)} />
          </div>

          <h3 className="text-sm font-semibold mt-4">Compensation</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input name="ol-ctc" label="CTC (Annual ₹) *" type="number" value={form.ctcAnnual} onChange={e => setField('ctcAnnual', e.target.value)} placeholder="e.g. 600000" />
          </div>

          <h3 className="text-sm font-semibold mt-4">Target & Incentive (optional — for sales roles)</h3>
          <div className="grid grid-cols-2 gap-3">
            <Input name="ol-target" label="Monthly Revenue Target (₹)" type="number" value={form.targetRevenue} onChange={e => setField('targetRevenue', e.target.value)} />
            <Input name="ol-incentive" label="Incentive %" type="number" value={form.incentivePercent} onChange={e => setField('incentivePercent', e.target.value)} />
          </div>

          <h3 className="text-sm font-semibold mt-4">Terms</h3>
          <div className="grid grid-cols-2 gap-3">
            <Input name="ol-probation" label="Probation Period" value={form.probationPeriod} onChange={e => setField('probationPeriod', e.target.value)} />
            <Input name="ol-notice" label="Notice Period" value={form.noticePeriod} onChange={e => setField('noticePeriod', e.target.value)} />
          </div>
          <div><label className="mb-1.5 block text-sm font-medium">Additional Terms</label><textarea className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white text-gray-900" rows={2} value={form.additionalTerms} onChange={e => setField('additionalTerms', e.target.value)} placeholder="Any additional clauses..." /></div>
        </div>
      </Modal>

      <Modal open={!!onboardingLinkFor} onClose={() => setOnboardingLinkFor(null)} title="Onboarding started" size="sm" footer={
        <div className="flex justify-end"><Button onClick={() => setOnboardingLinkFor(null)}>Done</Button></div>
      }>
        {onboardingLinkFor && (
          <div className="space-y-3 py-2 text-sm">
            <p>
              <strong>{onboardingLinkFor.name}</strong>'s onboarding record has been created. Share this E-Joining link with them to collect their personal details and documents:
            </p>
            <div className="flex items-center gap-2 rounded-md border bg-muted/30 p-2">
              <LinkIcon className="h-4 w-4 text-muted-foreground shrink-0" />
              <code className="text-xs break-all">{onboardingLinkFor.link}</code>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => { navigator.clipboard.writeText(onboardingLinkFor.link); toast.success('Link copied.'); }}
            >
              Copy Link
            </Button>
          </div>
        )}
      </Modal>

      <Card className="border-none shadow-sm">
        <CardHeader title={`Offer Letters (${offers.length})`} action={
          <Button size="sm" onClick={() => setShowNew(true)}><Plus className="h-4 w-4 mr-1" />New Offer Letter</Button>
        } />
        <CardBody className="p-0">
          <div className="overflow-auto max-h-[500px]">
            <table className="w-full text-sm text-left">
              <thead className="text-xs uppercase bg-muted/50 sticky top-0">
                <tr><th className="px-4 py-3">Candidate</th><th className="px-4 py-3">Designation</th><th className="px-4 py-3">Dept</th><th className="px-4 py-3">Joining</th><th className="px-4 py-3 text-right">CTC (Annual)</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Actions</th></tr>
              </thead>
              <tbody className="divide-y">
                {offers.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">No offer letters yet. Create one for a selected candidate.</td></tr>}
                {offers.map(o => (
                  <tr key={o._id} className="hover:bg-muted/20">
                    <td className="px-4 py-3 font-medium">
                      <div className="flex items-center gap-1.5">
                        {o.candidateName}
                        {o.sourceApplicationId && (
                          <Badge tone="info" className="text-[10px]" title="Auto-created from a Recruitment application">
                            <BriefcaseBusiness className="h-2.5 w-2.5" /> From Recruitment
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">{o.designation}</td>
                    <td className="px-4 py-3 capitalize">{o.department}</td>
                    <td className="px-4 py-3">{new Date(o.joiningDate).toLocaleDateString('en-IN')}</td>
                    <td className="px-4 py-3 text-right font-mono">₹{(o.ctcAnnual || (o.salaryStructure?.grossSalary || 0) * 12)?.toLocaleString('en-IN') || '0'}</td>
                    <td className="px-4 py-3">{statusBadge(o.status)}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={async () => {
                          // Try to open in the document viewer if linked to an onboarding candidate
                          if (o.sourceApplicationId) {
                            try {
                              const { data } = await api.get<{ candidateId: string }>(`/api/hr-offers/${o._id}/onboarding-candidate`);
                              if (data?.candidateId) {
                                navigate(`/hr-onboarding/${data.candidateId}/documents/offer_letter`);
                                return;
                              }
                            } catch {
                              // No linked onboarding candidate — fall through to raw preview
                            }
                          }
                          window.open(`/api/hr-offers/${o._id}/pdf`, '_blank');
                        }} title="Preview / Print"><Eye className="h-3.5 w-3.5" /></Button>
                        {o.status === 'draft' && <Button size="sm" variant="outline" onClick={() => { sendMut.mutateAsync(o._id).then((result: any) => { toast.success('Offer marked as sent.'); if (result?.emailDraft) { setEmailDraft(result.emailDraft); } }).catch(() => toast.error('Failed.')); }}><Send className="h-3.5 w-3.5 mr-1" />Send</Button>}
                        {o.status === 'sent' && (
                          <Button size="sm" variant="outline" onClick={() => handleAccept(o)} disabled={acceptMut.isPending} title="Record that the candidate accepted this offer">
                            <CheckCircle2 className="h-3.5 w-3.5 mr-1" />Mark Accepted
                          </Button>
                        )}
                        {o.status === 'accepted' && <span className="text-xs text-success-text font-medium inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" />Accepted</span>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>

      {emailDraft && (
        <EmailSendModal
          open={!!emailDraft}
          onClose={() => setEmailDraft(null)}
          initialTo={emailDraft.to}
          initialSubject={emailDraft.subject}
          initialHtml={emailDraft.html}
          onSent={() => setEmailDraft(null)}
        />
      )}
    </div>
  );
}


